import React, { FC, memo, useMemo } from 'react';

import * as Yup from 'yup';
import { getActions, withGlobal } from '../../global';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { Field, Form, Formik, FormikValues } from 'formik';
import Input from '../ui/Formik/Input';
import ReCaptcha from '../ui/ReCaptcha';
import Button from '../ui/Button';
import { ERoutes } from '../../types';
import { GlobalState } from '../../global/types';
import { USERNAME_REGEX } from '../../config';
import Radio from '../ui/Formik/Radio/Radio';

const initialValue = {
  username: '',
  email: '',
  token: '',
  kind: 'public', //   | private
  type: 'personal',
};

type StateProps = Pick<
  GlobalState,
  'authErrorAvailable' | 'authRefCode' | 'confirmData'
> & {
  confirmEmail?: string;
};

const CreateAccountForm: FC<StateProps> = ({
  authErrorAvailable,
  confirmEmail,
  authRefCode,
}) => {
  const { signUp } = getActions();

  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .test(
        'start_number',
        'Sorry, a username can`t start with a number.',
        (val) => isNaN(Number(val![0]))
      )
      .matches(/^(?=.*?[a-z]).{1,}$/, t('Validation.username_validation'))
      .required(t('Validation.required_username'))
      .min(5, t('Validation.username_min_length'))
      .max(32, t('Validation.username_max_length')),
    token: Yup.string().required(t('Validation.accept_captcha')),
  });

  const validateUsername = useMemo(() => {
    let errorText = '';
    if (!authErrorAvailable) return errorText;
    const { error, username } = authErrorAvailable || {};

    return username ? errorText : error || t('Errors.ThisUsernameUnavailable');
  }, [authErrorAvailable]);

  const handleSignUp = (values: FormikValues) => {
    const { username, kind, type, token } = values;
    signUp({
      username,
      email: confirmEmail!,
      kind,
      type,
      code: authRefCode,
      token,
    });
  };

  if (!confirmEmail)
    return <Navigate to={`${ERoutes.auth}/${ERoutes.signUp}`} replace />;

  return (
    <div
      id='auth-password-form'
      className='auth-password-center auth-password-form'
    >
      <div className='auth-form'>
        <div className='transition-block'>
          <Formik
            initialValues={initialValue}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              handleSignUp(values);
              resetForm();
              setSubmitting(false);
            }}
          >
            {({ isValid, dirty, isSubmitting }) => (
              <Form className='mb-3'>
                <h3>Create a Username</h3>
                <Field
                  name='username'
                  component={Input}
                  label={t('Registration.place_username')}
                  is_required
                  maxLength={32}
                  regexp={USERNAME_REGEX}
                  errorText={validateUsername}
                  helpText={t('Registration.username_help')}
                  isValidated
                />

                <h3>Choose your account</h3>
                <div className='radio-group mb-3'>
                  <Field
                    type='radio'
                    name='type'
                    value='personal'
                    component={Radio}
                    label={t('UserInfo.Personal')}
                    subLabel='Great for everyday use'
                    className='radio--no-mb'
                  />
                  <Field
                    type='radio'
                    name='type'
                    value='business'
                    component={Radio}
                    label={t('UserInfo.Business')}
                    subLabel='For your brand or company presence'
                    className='radio--no-mb'
                  />
                </div>

                <h3>Select your privacy</h3>
                <div className='radio-group mb-3'>
                  <Field
                    type='radio'
                    name='kind'
                    value='public'
                    component={Radio}
                    label={t('Registration.public')}
                    subLabel='Anyone can find and connect'
                    className='radio--no-mb'
                  />
                  <div>
                    <Field
                      type='radio'
                      name='kind'
                      value='private'
                      component={Radio}
                      label={t('Registration.private')}
                      subLabel='Stay completely anonymous'
                      className='radio--no-mb'
                    />
                    <p style={
                      {
                        paddingLeft: '60px',
                        paddingTop: '16px',
                        fontSize: '12px',
                      }
                    }
                    className="subLabel"
                    >
                      Strongly advised for children for maximum protection from unwanted interactions — username won’t appear in search and only approved contacts can chat
                    </p>
                  </div>
                </div>

                <Field name='token' action='signup' component={ReCaptcha} />

                <Button
                  className='mb-3'
                  type='submit'
                  fullWidth
                  isShiny
                  disabled={!dirty || !isValid || isSubmitting}
                >
                  {t('Login.sign_up')}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const { authErrorAvailable, confirmData, authRefCode } = global;
    return {
      authErrorAvailable,
      confirmEmail: confirmData?.email,
      authRefCode,
    };
  })(CreateAccountForm)
);
